import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown, FaChevronUp, FaSearch, FaArrowLeft } from 'react-icons/fa';
import { Button, Link } from '@nextui-org/react';
import { libraryData } from './libraryData';
import LibraryCard from './LibraryCard';
import { getFullHeightAccordionImage } from './fullHeightAccordionImageMapping';
import MyInput from './SearchInput';
import { useTheme } from 'next-themes';
import LibraryOverview from './LibraryOverview';
import { mergeLibrarySearchHits } from './librarySearch';
import { normalizeText } from '../utils';

// Lazy loading image component
const LazyImage: React.FC<{ src: string; alt: string; className: string }> = ({ src, alt, className }) => {
    const [imageSrc, setImageSrc] = useState<string>('');
    const [imageRef, setImageRef] = useState<HTMLImageElement | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        let observer: IntersectionObserver;

        if (imageRef) {
            observer = new IntersectionObserver(
                entries => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            setImageSrc(src);
                            observer.unobserve(imageRef);
                        }
                    });
                },
                { threshold: 0.1, rootMargin: '50px' }
            );
            observer.observe(imageRef);
        }

        return () => {
            if (observer && imageRef) {
                observer.unobserve(imageRef);
            }
        };
    }, [imageRef, src]);

    return (
        <div className='relative w-[300px] h-[300px] rounded-2xl border-4 border-white/80 backdrop-blur-sm overflow-hidden shadow-2xl bg-black'>
            {!isLoaded && (
                <div className='absolute inset-0 bg-gradient-to-br from-gray-400 to-gray-600 animate-pulse'/>
            )}
            <img
                ref={setImageRef}
                src={imageSrc}
                alt={alt}
                className={`${className} ${!isLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-500 rounded-xl`}
                onLoad={() => setIsLoaded(true)}
            />
        </div>
    );
};

// Transliteration mapping for common Sanskrit terms
const transliterationMap: Record<string, string> = {
    // Bidirectional mapping - lowercase for case-insensitive matching
    'krishna': 'kṛṣṇa',
    'kṛṣṇa': 'krishna',
    'vishnu': 'viṣṇu',
    'viṣṇu': 'vishnu',
    'shiva': 'śiva',
    'śiva': 'shiva',
    'bhagavad gita': 'bhagavad-gītā',
    'bhagavad-gītā': 'bhagavad gita',
    'bhagavad gītā': 'bhagavad gita', // Alternative form with different spacing
};

// Get transliteration alternatives for a search term
const getTransliterationAlternatives = (searchTerm: string): string[] => {
    const normalizedSearch = searchTerm.toLowerCase().trim();
    const alternatives = [searchTerm]; // Always include original

    // Check for exact match
    if (transliterationMap[normalizedSearch]) {
        alternatives.push(transliterationMap[normalizedSearch]);
    }

    // Check for partial matches (for compound terms)
    Object.entries(transliterationMap).forEach(([key, value]) => {
        if (normalizedSearch.includes(key) && !alternatives.includes(value)) {
            // Replace the matched part with its transliteration
            const alternativeTerm = searchTerm.toLowerCase().replace(key, value);
            alternatives.push(alternativeTerm);
        }
    });

    // Remove duplicates using filter and indexOf
    return alternatives.filter((item, index) => alternatives.indexOf(item) === index);
};

interface AccordionSection {
    id: string;
    title: string;
    content: string;
    color: string;
    image: string;
    items: Array<{
        subTopicName: string;
        youtubeLink: string;
    }>;
}

interface FlattenedSearchTopic {
    themeNames: string[];
    subTopicName: string;
    youtubeLink: string;
}

// Generate gradient colors from Tailwind palette - more variety for 57 sections
const colorGradients = [
    'from-purple-600 to-pink-600',
    'from-orange-600 to-red-600',
    'from-yellow-600 to-orange-600',
    'from-pink-600 to-rose-600',
    'from-amber-600 to-yellow-600',
    'from-violet-600 to-purple-600',
    'from-rose-600 to-pink-600',
    'from-fuchsia-600 to-pink-600',
    // 'from-red-600 to-orange-600',
    'from-yellow-600 to-red-600',
    'from-pink-600 to-purple-600',
    'from-orange-600 to-yellow-600',
    'from-amber-600 to-orange-600',
    'from-violet-600 to-pink-600',
    'from-rose-600 to-red-600',
    'from-fuchsia-600 to-purple-600',
    // New color combinations from the palettes
    'from-[#662400] to-[#B33F00]', // Dark brown to orange
    'from-[#B33F00] to-[#FF7733]', // Orange gradient
    'from-[#C43302] to-[#EDAA25]', // Red-orange to gold
    'from-[#C43302] to-[#662400]', // Red-orange to dark brown
    'from-[#EDAA25] to-[#FF7733]', // Gold to bright orange
    'from-purple-600 to-rose-600', // Purple to rose
    'from-orange-600 to-pink-600', // Orange to pink
    // 'from-red-600 to-purple-600', // Red to purple
    'from-yellow-600 to-pink-600', // Yellow to pink
    'from-amber-600 to-red-600', // Amber to red
    'from-[#B33F00] to-[#C43302]', // Orange to red-orange
    'from-[#FF7733] to-[#EDAA25]', // Bright orange to gold
    'from-[#662400] to-[#C43302]', // Dark brown to red-orange
    'from-[#EDAA25] to-[#B33F00]', // Gold to orange
    'from-[#C43302] to-[#FF7733]', // Red-orange to bright orange
    'from-purple-600 to-orange-600', // Purple to orange
    'from-pink-600 to-amber-600', // Pink to amber
    'from-rose-600 to-yellow-600', // Rose to yellow
    'from-violet-600 to-red-600', // Violet to red
    'from-fuchsia-600 to-orange-600', // Fuchsia to orange
];

const FullHeightAccordions: React.FC = () => {
    const { theme } = useTheme();
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
    const [activeSection, setActiveSection] = useState<string>('');
    const [scrollY, setScrollY] = useState(0);
    const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
    const [visibleDotIndices, setVisibleDotIndices] = useState<number[]>([]);

    // Search state
    const [searchValue, setSearchValue] = useState<string>('');
    const [onResetResult, setOnResetResult] = useState<boolean>(false);
    const hasSearchQuery = searchValue.trim().length > 0;

    // View mode state
    const [viewMode, setViewMode] = useState<'overview' | 'detail'>('overview');
    const [selectedSectionId, setSelectedSectionId] = useState<string>('');
    const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
    const [showBackButton, setShowBackButton] = useState<boolean>(false);

    // Performance optimizations:
    // 1. LazyImage component loads images only when they're in viewport
    // 2. Content is unmounted when sections are collapsed (AnimatePresence mode="wait")

    // Reset onResetResult flag after MyInput component has used it
    useEffect(() => {
        if (onResetResult) {
            setOnResetResult(false);
        }
    }, [onResetResult]);

    // Auto-focus search input on mount
    useEffect(() => {
        // Small delay to ensure input is rendered
        setTimeout(() => {
            const searchInput = document.querySelector('input[type="text"]');
            if (searchInput) {
                (searchInput as HTMLInputElement).focus();
            }
        }, 100);
    }, []);

    // Generate sections from libraryData - memoized to prevent recreating on every render
    const sections: AccordionSection[] = useMemo(() => {
        return Object.entries(libraryData).map(([key, items], index) => {
            return ({
                id: `section-${index}`,
                title: key,
                content: `${items.length} topics`,
                color: colorGradients[index % colorGradients.length],
                image: getFullHeightAccordionImage(key),
                items: items
            })
        })
    }, []);

    // Handle hash navigation
    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash.slice(1); // Remove #
            if (hash) {
                const sectionIndex = sections.findIndex(s =>
                    s.title.toLowerCase().replace(/\s+/g, '-') === hash.toLowerCase()
                );
                if (sectionIndex !== -1) {
                    setSelectedSectionId(`section-${sectionIndex}`);
                    setViewMode('detail');
                    // Scroll to top when entering detail view
                    window.scrollTo(0, 0);
                    setShowBackButton(true); // Show back button immediately
                }
            } else {
                setViewMode('overview');
                setSelectedSectionId('');
                setShowBackButton(false);
            }
        };

        // Check initial hash
        handleHashChange();

        // Listen for hash changes
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, [sections]);

    // Handle section click from overview
    const handleSectionClick = (sectionId: string) => {
        const section = sections.find(s => s.id === sectionId);
        if (section) {
            setIsTransitioning(true);
            setSelectedSectionId(sectionId);
            setShowBackButton(false); // Hide button during transition

            // Update URL with hash
            const sectionHash = section.title.toLowerCase().replace(/\s+/g, '-');
            window.location.hash = sectionHash;

            // Animate transition
            setTimeout(() => {
                setViewMode('detail');
                setIsTransitioning(false);
                window.scrollTo(0, 0);
                setShowBackButton(true); // Show back button immediately
            }, 500);
        }
    };

    // Handle back to overview
    const handleBackToOverview = () => {
        setIsTransitioning(true);
        setShowBackButton(false); // Hide button immediately
        window.location.hash = '';

        setTimeout(() => {
            setViewMode('overview');
            setSelectedSectionId('');
            setIsTransitioning(false);
            setExpandedSections(new Set()); // Collapse all sections
            window.scrollTo(0, 0);
        }, 500);
    };

    // Filter sections based on search
    const filteredSections: AccordionSection[] = useMemo(() => {
        if (!hasSearchQuery) {
            return sections;
        }

        // Get all transliteration alternatives for the search term
        const searchAlternatives = getTransliterationAlternatives(searchValue);
        const normalizedAlternatives = searchAlternatives.map(alt => normalizeText(alt));

        // Filter sections that have matching items
        return sections
            .map(section => ({
                ...section,
                items: section.items.filter(item => {
                    const normalizedSubTopicName = normalizeText(item.subTopicName);
                    // Check if any of the normalized alternatives match
                    return normalizedAlternatives.some(normalizedAlt =>
                        normalizedSubTopicName.includes(normalizedAlt)
                    );
                })
            }))
            .filter(section => section.items.length > 0)
            .map((section, index) => ({
                ...section,
                id: `section-${index}`, // Re-index for proper scrolling
                content: `${section.items.length} topics found`
            }));
    }, [hasSearchQuery, searchValue, sections]);

    const flattenedSearchTopics: FlattenedSearchTopic[] = useMemo(() => {
        if (!hasSearchQuery) {
            return [];
        }

        const hits = filteredSections.flatMap(section =>
            section.items.map(item => ({
                themeName: section.title,
                subTopicName: item.subTopicName,
                youtubeLink: item.youtubeLink,
            }))
        );
        const catalog = sections.flatMap(section =>
            section.items.map(item => ({
                themeName: section.title,
                subTopicName: item.subTopicName,
                youtubeLink: item.youtubeLink,
            }))
        );

        return mergeLibrarySearchHits(hits, catalog);
    }, [hasSearchQuery, filteredSections, sections]);

    // Use filtered sections if searching, otherwise use all sections
    // In detail view, only show the selected section
    const displaySections = useMemo(() => {
        if (viewMode === 'detail' && selectedSectionId) {
            // Use filtered sections if searching, otherwise use all sections
            const sectionsToFilter = searchValue ? filteredSections : sections;
            return sectionsToFilter.filter(s => s.id === selectedSectionId);
        }
        return searchValue ? filteredSections : sections;
    }, [viewMode, selectedSectionId, searchValue, filteredSections, sections]);

    // Toggle section expansion
    const toggleSection = (sectionId: string) => {
        setExpandedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sectionId)) {
                newSet.delete(sectionId);
            } else {
                newSet.add(sectionId);
            }
            return newSet;
        });
    };

    // Reset search
    const handleResetSearch = () => {
        setSearchValue('');
        setOnResetResult(true);
        setExpandedSections(new Set()); // Collapse all sections
    };

    // Jump to section
    const jumpToSection = (sectionId: string) => {
        const sectionIndex = displaySections.findIndex(s => s.id === sectionId);
        if (sectionIndex !== -1) {
            // Each section takes up 100vh, so scroll to index * viewport height
            const scrollTarget = sectionIndex * window.innerHeight;
            window.scrollTo({
                top: scrollTarget,
                behavior: 'smooth'
            });
        }
    };

    // Scroll to top function
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // Track active section on scroll
    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY + window.innerHeight / 2;
            setScrollY(window.scrollY);

            // Show/hide scroll to top button
            if (window.scrollY > 300) {
                setShowScrollTop(true);
            } else {
                setShowScrollTop(false);
            }

            let currentActiveIndex = 0;
            displaySections.forEach((section, index) => {
                const element = document.getElementById(section.id);
                if (element) {
                    const { top, bottom } = element.getBoundingClientRect();
                    const elementTop = top + window.scrollY;
                    const elementBottom = bottom + window.scrollY;

                    if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
                        setActiveSection(section.id);
                        currentActiveIndex = index;
                    }
                }
            });

            // Calculate which dots to show (active section ± 3)
            const dotsToShow = 7; // Show 7 dots total (3 above, active, 3 below)
            const halfDots = Math.floor(dotsToShow / 2);

            let startIndex = Math.max(0, currentActiveIndex - halfDots);
            let endIndex = Math.min(displaySections.length - 1, currentActiveIndex + halfDots);

            // Adjust if we're near the edges
            if (startIndex === 0) {
                endIndex = Math.min(dotsToShow - 1, displaySections.length - 1);
            } else if (endIndex === displaySections.length - 1) {
                startIndex = Math.max(0, displaySections.length - dotsToShow);
            }

            const visibleIndices = [];
            for (let i = startIndex; i <= endIndex; i++) {
                visibleIndices.push(i);
            }
            setVisibleDotIndices(visibleIndices);
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Check initial position

        return () => window.removeEventListener('scroll', handleScroll);
    }, [displaySections]);

    return (
        <div className='relative overflow-x-hidden'>
            {/* Transition overlay */}
            <AnimatePresence>
                {isTransitioning && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className='fixed inset-0 bg-white dark:bg-gray-900 z-[100] pointer-events-none'
                    />
                )}
            </AnimatePresence>

            {/* Scroll to top button only */}
            <AnimatePresence>
                {showScrollTop && !showBackButton && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.2 }}
                        className='fixed bottom-8 right-8 z-[9999]'
                    >
                        <Button
                            isIconOnly={true}
                            className='bg-white text-darkBlue shadow-lg border-2 border-darkBlue hover:bg-darkBlue hover:text-white transition-all duration-200'
                            size='lg'
                            onClick={scrollToTop}
                        >
                            <FaChevronUp className='w-4 h-4'/>
                        </Button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Overview Mode */}
            <AnimatePresence mode='wait'>
                {viewMode === 'overview' && !isTransitioning && (
                    <motion.div
                        key='overview'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        className='min-h-screen bg-white dark:bg-gray-900'
                    >
                        {/* Search Section - Like MainCourses */}
                        <div className='md:mx-24 md:my-10 m-8'>
                            {searchValue && (
                                <Link
                                    className='flex justify-start mb-1.5 cursor-pointer text-lg font-bold text-darkBlue dark:text-lightBlue'
                                    onClick={handleResetSearch}
                                >
                                    Reset results
                                </Link>
                            )}
                            <div className='overflow-hidden h-[80px] mb-5'>
                                <motion.div
                                    initial={{ translateY: "-100%", opacity: 0 }}
                                    animate={{ translateY: "0%", opacity: 1 }}
                                    style={{ overflow: "hidden" }}
                                    transition={{ type: "spring", duration: 0.7 }}
                                >
                                    <MyInput
                                        onChangeValue={(value) => setSearchValue(value)}
                                        shouldDisable={false}
                                        searchDescription='Search in topics...'
                                        onResetResult={onResetResult}
                                        value={searchValue}
                                    />
                                </motion.div>
                            </div>
                        </div>

                        {hasSearchQuery ? (
                            <main className='md:mx-24 md:my-10 m-6 sm:m-8 pb-14'>
                                {flattenedSearchTopics.length > 0 ? (
                                    <>
                                        <motion.div
                                            initial={{ opacity: 0, y: -20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className='text-center pb-8 px-4'
                                        >
                                            <h2 className='text-3xl md:text-4xl font-bold text-darkBlue dark:text-white mb-2'>
                                                Search Results for
                                                <span className='text-lightOrange'> "{searchValue}"</span>
                                            </h2>
                                            <p className='text-gray-600 dark:text-gray-300'>
                                                Found <span className='font-inter-numbers'>{flattenedSearchTopics.length}</span> topics
                                            </p>
                                        </motion.div>

                                        <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 xl:gap-6'>
                                            {flattenedSearchTopics.map((item, index) => (
                                                <motion.div
                                                    key={`${item.youtubeLink || item.subTopicName}-${index}`}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: Math.min(index * 0.02, 0.35) }}
                                                    className='library-card h-full'
                                                >
                                                    <LibraryCard
                                                        youtubeLink={item.youtubeLink}
                                                        subTopicName={item.subTopicName}
                                                        themeNames={item.themeNames}
                                                    />
                                                </motion.div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <div className='min-h-[40vh] flex items-center justify-center'>
                                        <motion.div
                                            initial={{ opacity: 0, y: 30, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            transition={{ duration: 0.4 }}
                                            className='text-center px-8 py-12 bg-gray-100 dark:bg-gray-800 rounded-3xl border border-gray-200 dark:border-gray-700 max-w-2xl mx-4 shadow-xl'
                                        >
                                            <h3 className='text-3xl text-darkBlue dark:text-white font-bold mb-3'>No topics found</h3>
                                            <p className='text-lg text-gray-600 dark:text-gray-300 mb-6'>
                                                We couldn't find any topics matching
                                                <span className='text-darkBlue dark:text-lightBlue font-semibold'> "{searchValue}"</span>
                                            </p>
                                            <button
                                                className='text-lg font-bold text-darkBlue dark:text-lightBlue cursor-pointer hover:underline transition-all'
                                                onClick={handleResetSearch}
                                            >
                                                Reset results
                                            </button>
                                        </motion.div>
                                    </div>
                                )}
                            </main>
                        ) : (
                            <LibraryOverview
                                sections={sections}
                                onSectionClick={handleSectionClick}
                                searchValue={searchValue}
                                onResetSearch={handleResetSearch}
                            />
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Detail Mode */}
            {viewMode === 'detail' && (
                <>
                    {/* Search Results Header */}
                    {searchValue && displaySections.length > 0 && (
                        <div
                            className='fixed left-1/2 transform -translate-x-1/2 z-50'
                            style={{ top: '123px' }}
                        >
                            <motion.div
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ delay: 0.3, duration: 0.3 }}
                                className='text-center px-8 py-4 bg-black/60 backdrop-blur-xl 
                                         rounded-3xl border border-white/20 shadow-2xl
                                         w-auto max-w-[90vw] md:max-w-2xl'
                            >
                                <h2 className='text-2xl md:text-3xl text-white font-semibold mb-2 drop-shadow-2xl'>
                                    Search Results for
                                    <span className='text-darkBlue'> "{searchValue}"</span>
                                </h2>
                                <p className='text-white/70 text-sm mb-3'>
                                    Found {displaySections.reduce((acc, section) => acc + section.items.length, 0)} topics{viewMode === 'detail' && selectedSectionId ? '' : ` in ${displaySections.length} sections`}
                                </p>
                                <Link
                                    className='text-darkBlue underline hover:no-underline cursor-pointer
                                             text-lg font-bold transition-all'
                                    onClick={handleResetSearch}
                                >
                                    Reset results
                                </Link>
                            </motion.div>
                        </div>
                    )}

                    {/* Navigation dots - only show if multiple sections in normal scroll mode */}
                    {displaySections.length > 1 && !selectedSectionId && (
                        <div className='fixed right-8 top-1/2 -translate-y-1/2 z-50 hidden lg:block'>
                            <div className='flex flex-col items-center'>
                                {/* Top fade indicator */}
                                {visibleDotIndices.length > 0 && visibleDotIndices[0] > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className='mb-2 text-white/40 text-xs'
                                    >
                                        ···
                                    </motion.div>
                                )}

                                <div className='flex flex-col gap-4'>
                                    <AnimatePresence mode='sync'>
                                        {displaySections.map((section, index) => (
                                            visibleDotIndices.includes(index) && (
                                                <motion.button
                                                    key={section.id}
                                                    initial={{ opacity: 0, scale: 0.5, x: 20 }}
                                                    animate={{ opacity: 1, scale: 1, x: 0 }}
                                                    exit={{ opacity: 0, scale: 0.5, x: 20 }}
                                                    transition={{ duration: 0.2 }}
                                                    onClick={() => jumpToSection(section.id)}
                                                    className={`w-3 h-3 rounded-full transition-all duration-300 ${activeSection === section.id
                                                        ? 'bg-white scale-150'
                                                        : 'bg-white/50 hover:bg-white/70'
                                                    }`}
                                                    aria-label={`Jump to ${section.title}`}
                                                />
                                            )
                                        ))}
                                    </AnimatePresence>
                                </div>

                                {/* Bottom fade indicator */}
                                {visibleDotIndices.length > 0 && visibleDotIndices[visibleDotIndices.length - 1] < displaySections.length - 1 && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className='mt-2 text-white/40 text-xs'
                                    >
                                        ···
                                    </motion.div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Accordion sections */}
                    <div className='transition-all duration-300'>
                        {displaySections.map((section, index) => (
                            <div
                                key={`wrapper-${section.id}`}
                                className='relative'
                                style={{
                                    minHeight: '100vh',
                                }}
                            >
                                <div
                                    id={section.id}
                                    className='min-h-screen sticky'
                                    style={{
                                        top: index === 0 ? '0' : `${Math.min(index * 2, 80)}vh`,
                                        zIndex: displaySections.length - index
                                    }}
                                >
                                    {/* Background with gradient and mix-blend-mode */}
                                    <div
                                        className={`absolute inset-0 bg-gradient-to-br ${section.color} mix-blend-multiply ${theme === 'dark' ? 'opacity-40' : 'opacity-100'
                                        }`}
                                    />

                                    {/* Dark overlay for better text readability */}
                                    <div className={`absolute inset-0 ${theme === 'dark' ? 'bg-black/10' : 'bg-black/20'
                                    }`}/>

                                    {/* Content */}
                                    <div className='relative z-10 min-h-screen flex flex-col justify-center items-center p-8'>
                                        <motion.div
                                            initial={{ opacity: 0, y: 50 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.8, delay: index * 0.1 }}
                                            className='w-full max-w-6xl'
                                        >
                                            {/* Back to Overview button - aligned with content */}
                                            {showBackButton && (
                                                <div className='mb-8'>
                                                    <Button
                                                        isIconOnly
                                                        className='bg-white text-darkBlue shadow-lg border-2 border-darkBlue hover:bg-darkBlue hover:text-white transition-all duration-200'
                                                        size='lg'
                                                        onClick={handleBackToOverview}
                                                    >
                                                        <FaArrowLeft className='w-4 h-4'/>
                                                    </Button>
                                                </div>
                                            )}

                                            {/* Section header */}
                                            <div className='text-center mb-8'>
                                                {/* Parallax Image - hidden when expanded */}
                                                <AnimatePresence mode='wait'>
                                                    {!expandedSections.has(section.id) && (
                                                        <motion.div
                                                            initial={{ opacity: 0, height: 0 }}
                                                            animate={{
                                                                opacity: 1,
                                                                height: 'auto',
                                                                transition: {
                                                                    height: { duration: 0.5, ease: "easeOut" },
                                                                    opacity: { duration: 0.3 }
                                                                }
                                                            }}
                                                            exit={{
                                                                opacity: 0,
                                                                height: 0,
                                                                transition: {
                                                                    height: { duration: 0.4, ease: "easeInOut" },
                                                                    opacity: { duration: 0.2 }
                                                                }
                                                            }}
                                                            // className='overflow-hidden'
                                                        >
                                                            <motion.div
                                                                initial={{ y: 50 }}
                                                                animate={{ y: 0 }}
                                                                exit={{ scale: 0.9, y: -20 }}
                                                                transition={{ duration: 0.3 }}
                                                                className='mb-8 relative flex justify-center'
                                                            >
                                                                <motion.div
                                                                    style={{
                                                                        transform: `translateY(${
                                                                            // Parallax effect based on scroll position
                                                                            -(scrollY - (index * window.innerHeight)) * 0.3
                                                                        }px) scale(${activeSection === section.id ? 1.05 : 1})`,
                                                                    }}
                                                                    transition={{ scale: { duration: 0.3 } }}
                                                                    className='inline-block'
                                                                >
                                                                    <LazyImage
                                                                        src={section.image}
                                                                        alt={section.title}
                                                                        className='w-full h-full object-cover object-top'
                                                                    />
                                                                </motion.div>
                                                            </motion.div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>

                                                <h1 className='text-5xl md:text-7xl font-bold text-white mb-4 drop-shadow-2xl'>
                                                    {section.title}
                                                </h1>
                                                <p className='text-xl md:text-2xl text-white/90 drop-shadow-lg'>
                                                    {section.content}
                                                </p>
                                            </div>

                                            {/* Expandable content */}
                                            <div className='mt-12'>
                                                <button
                                                    onClick={() => toggleSection(section.id)}
                                                    className='w-full bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20
                                           hover:bg-white/20 transition-all duration-300 group'
                                                >
                                                    <div className='flex justify-between items-center'>
                                                        <span className='text-white text-lg font-semibold'>
                                                            Explore {section.title}
                                                        </span>
                                                        <motion.div
                                                            animate={{ rotate: expandedSections.has(section.id) ? 180 : 0 }}
                                                            transition={{ duration: 0.4, ease: "easeInOut" }}
                                                        >
                                                            <FaChevronDown className='text-white text-2xl'/>
                                                        </motion.div>
                                                    </div>
                                                </button>

                                                <AnimatePresence mode='wait'>
                                                    {expandedSections.has(section.id) && (
                                                        <motion.div
                                                            key={`content-${section.id}`}
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{
                                                                height: 'auto',
                                                                opacity: 1,
                                                                transition: {
                                                                    height: {
                                                                        duration: 0.5,
                                                                        ease: "easeOut",
                                                                        delay: 0.1 // Start slightly after image starts fading
                                                                    },
                                                                    opacity: {
                                                                        duration: 0.4,
                                                                        delay: 0.2 // Fade in while image is fading out
                                                                    }
                                                                }
                                                            }}
                                                            exit={{
                                                                height: 0,
                                                                opacity: 0,
                                                                transition: {
                                                                    height: { duration: 0.3, ease: "easeInOut" },
                                                                    opacity: { duration: 0.2 }
                                                                }
                                                            }}
                                                            className='overflow-hidden'
                                                        >

                                                            <div
                                                                className='mt-4 max-h-[60vh] overflow-y-auto pr-4 custom-scrollbar mb-10'
                                                                style={{
                                                                    scrollbarWidth: 'thin',
                                                                    scrollbarColor: 'rgba(255, 255, 255, 0.5) rgba(255, 255, 255, 0.1)'
                                                                }}
                                                            >
                                                                <div className='grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3'>
                                                                    {section.items?.map((item, itemIndex) => (
                                                                        <motion.div
                                                                            key={item.youtubeLink || itemIndex}
                                                                            initial={{ opacity: 0, y: 20 }}
                                                                            animate={{ opacity: 1, y: 0 }}
                                                                            exit={{ opacity: 0, y: -20 }}
                                                                            transition={{ delay: Math.min(itemIndex * 0.03, 0.5) }}
                                                                        >
                                                                            <LibraryCard
                                                                                youtubeLink={item.youtubeLink}
                                                                                subTopicName={item.subTopicName}
                                                                            />
                                                                        </motion.div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>

                                        </motion.div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* No results message */}
                    {searchValue && displaySections.length === 0 && (
                        <div className='min-h-screen flex items-center justify-center relative'>
                            {/* Background gradient for no results */}
                            <div className={`absolute inset-0 bg-gradient-to-br from-purple-900/50 to-pink-900/50 mix-blend-multiply ${theme === 'dark' ? 'opacity-40' : 'opacity-100'
                            }`}/>

                            <motion.div
                                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ duration: 0.5, ease: "easeOut" }}
                                className='text-center px-8 py-16 bg-white/10 backdrop-blur-xl 
                                         rounded-3xl border border-white/20 max-w-2xl mx-4 shadow-2xl relative z-10'
                            >
                                {/* Animated icon */}
                                <motion.div
                                    animate={{ rotate: [0, 10, -10, 0] }}
                                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                                    className='mb-8'
                                >
                                    <FaSearch className='text-6xl text-white/50 mx-auto'/>
                                </motion.div>

                                <h3 className='text-4xl text-white font-bold mb-4'>No results found</h3>
                                <p className='text-xl text-white/80 mb-8 max-w-md mx-auto'>
                                    We couldn't find any topics matching
                                    <span className='text-darkBlue font-semibold'> "{searchValue}"</span>
                                </p>

                                <div className='flex flex-col sm:flex-row gap-4 justify-center'>
                                    <Button
                                        onClick={handleResetSearch}
                                        className='bg-white/20 text-white border border-white/30
                                               hover:bg-white/30 transition-all font-semibold'
                                        size='lg'
                                    >
                                        Clear Search
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            // Scroll to top where search bar is
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                            // Focus on search input
                                            setTimeout(() => {
                                                const searchInput = document.querySelector('input[type="text"]');
                                                if (searchInput) {
                                                    (searchInput as HTMLInputElement).focus();
                                                }
                                            }, 500);
                                        }}
                                        color='primary'
                                        className='bg-darkBlue dark:bg-lightGrey'
                                        size='lg'
                                        radius='lg'
                                        variant={theme === 'light' ? 'solid' : 'bordered'}
                                    >
                                        Try Again
                                    </Button>
                                </div>
                            </motion.div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default FullHeightAccordions; 
